/**
 * Every cell of the Add row, when it is not being edited.
 *
 * Installed through `cellRendererSelector` on **every** column, so the pinned row
 * never reaches a column's own renderer. Each of those would otherwise draw
 * something plausible and wrong over a row that has no data: a pending column's
 * dots, an image column's broken logo, the star column's star. The pinned row
 * is an input, not a symbol, and this is the one place that says so.
 *
 * Three things, by column:
 *
 * - **The Symbol cell** — the grey hint, `+ Add…`. A click on it starts the
 *   editor (`singleClickEdit` on that column), and so does a letter typed with
 *   it focused.
 * - **The message cell** — the last rejection, in the negative colour. Which
 *   column that is lives in `addRowMessageColumn`.
 * - **Everything else** — nothing.
 *
 * `var(--qf-*)` only, like every other cell here: the island never owns a theme.
 */

import type { CSSProperties } from "react";
import type { CustomCellRendererProps } from "ag-grid-react";

import { ADD_ROW_MESSAGE, addRowMessageColumn } from "../lib/add-row.js";

export interface AddRowCellParams {
  /** The column that takes the ticker — the grid's id field. */
  symbolField: string;
  /** The hint, read at render so a host can change it without a new column. */
  placeholder: () => string;
}

const HINT: CSSProperties = {
  color: "var(--qf-text-muted)",
  fontStyle: "italic",
  cursor: "text",
};

const MESSAGE: CSSProperties = {
  color: "var(--qf-negative)",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

export function AddRowCell(props: CustomCellRendererProps & AddRowCellParams) {
  const colId = props.column?.getColId();
  if (!colId) return null;

  if (colId === props.symbolField) return <span style={HINT}>{props.placeholder()}</span>;

  const message = props.data?.[ADD_ROW_MESSAGE];
  if (typeof message !== "string" || !message) return null;
  // Read at render rather than passed in: which columns are shown changes under
  // the row (the picker, a drag), and every new message is new row data, which
  // re-renders the row's cells — so each message lands on the current answer.
  const displayed = props.api.getAllDisplayedColumns().map((column) => column.getColId());
  if (addRowMessageColumn(displayed, props.symbolField) !== colId) return null;
  return (
    <span style={MESSAGE} title={message} role="alert">
      ✗ {message}
    </span>
  );
}
